import {
  createDisposable,
  Disposable,
  disposedDisposable,
} from "./Disposable";

export type StoreUpdater<State> = (current: Readonly<State>) => State;
export type StoreSubscriber<State> = (
  current: Readonly<State>,
  previous: Readonly<State>,
) => void;

export interface StoreSubscriptionOptions {
  emitCurrent?: boolean;
  signal?: AbortSignal;
}

type SubscriberRecord<State> = {
  subscriber: StoreSubscriber<State>;
  subscription: Disposable;
};

export class ObservableStore<State> implements Disposable {
  private readonly subscribers = new Set<SubscriberRecord<State>>();
  private _disposed = false;

  constructor(private _value: State) {}

  get disposed(): boolean {
    return this._disposed;
  }

  get value(): Readonly<State> {
    return this._value;
  }

  set(next: State | StoreUpdater<State>): boolean {
    this.assertActive();
    const previous = this._value;
    const value =
      typeof next === "function"
        ? (next as StoreUpdater<State>)(previous)
        : next;
    if (Object.is(previous, value)) return false;

    this._value = value;
    this.notify(value, previous);
    return true;
  }

  update(updater: StoreUpdater<State>): boolean {
    return this.set(updater);
  }

  subscribe(
    subscriber: StoreSubscriber<State>,
    options: StoreSubscriptionOptions = {},
  ): Disposable {
    this.assertActive();
    if (options.signal?.aborted) return disposedDisposable();

    let abortHandler: (() => void) | undefined;
    const record = {
      subscriber,
      subscription: undefined as unknown as Disposable,
    };
    const subscription = createDisposable(() => {
      this.subscribers.delete(record);
      if (abortHandler && options.signal) {
        options.signal.removeEventListener("abort", abortHandler);
      }
    });
    record.subscription = subscription;
    this.subscribers.add(record);

    if (options.signal) {
      abortHandler = () => subscription.dispose();
      options.signal.addEventListener("abort", abortHandler, { once: true });
    }
    if (options.emitCurrent) subscriber(this._value, this._value);

    return subscription;
  }

  dispose(): void {
    if (this._disposed) return;
    this._disposed = true;
    for (const record of [...this.subscribers]) {
      record.subscription.dispose();
    }
  }

  private notify(current: State, previous: State): void {
    const errors: unknown[] = [];
    for (const record of [...this.subscribers]) {
      if (record.subscription.disposed) continue;
      try {
        record.subscriber(current, previous);
      } catch (error) {
        errors.push(error);
      }
    }

    if (errors.length === 1) throw errors[0];
    if (errors.length > 1) {
      throw new AggregateError(errors, "ObservableStore subscribers failed");
    }
  }

  private assertActive(): void {
    if (this._disposed) throw new Error("ObservableStore has been disposed");
  }
}
